'use client';

import React from 'react';
import { Button, Card } from '@heroui/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the whole app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error('Error Boundary caught an error:', error, errorInfo);

    // You could also log to an error reporting service here
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="max-w-md p-6">
            <div className="text-center">
              <h1 className="mb-4 text-2xl font-bold text-danger">Oops! Something went wrong</h1>

              <p className="mb-4 text-muted">We're sorry, but something unexpected happened. Please try refreshing the page.</p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-4 text-left">
                  <summary className="cursor-pointer font-semibold text-warning">Error Details (Development Only)</summary>
                  <pre className="mt-2 overflow-auto rounded bg-muted-100 p-2 text-xs">{this.state.error.toString()}</pre>
                </details>
              )}

              <div className="flex justify-center gap-2">
                <Button variant="primary" onPress={() => window.location.reload()}>
                  Refresh Page
                </Button>

                <Button variant="secondary" onPress={this.handleReset}>
                  Try Again
                </Button>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Single default export for Turbopack compatibility
export default ErrorBoundary;
